import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import { AdminMembersReportProductsTableCurriculumCompletionFilterState } from "@/admin/insights/member-reports/tabs/products/AdminMembersReportProductsTableCurriculumCompletionFilter"
import AdminMembersReportProductsTableFilterTags from "@/admin/insights/member-reports/tabs/products/AdminMembersReportProductsTableFilterTags"
import AdminMembersReportProductsTableRow, {
  AdminMembersReportProductsTableRowSkeleton,
} from "@/admin/insights/member-reports/tabs/products/AdminMembersReportProductsTableRow"
import AdminMembersReportProductsTableToolbar, {
  AdminMembersReportProductsTableToolbarSkeleton,
} from "@/admin/insights/member-reports/tabs/products/AdminMembersReportProductsTableToolbar"
import { AdminMembersReportProductsTablePaginationQuery } from "@/admin/insights/member-reports/tabs/products/__generated__/AdminMembersReportProductsTablePaginationQuery.graphql"
import {
  AdminMembersReportProductsTableQuery,
  ProductMembershipOrder,
} from "@/admin/insights/member-reports/tabs/products/__generated__/AdminMembersReportProductsTableQuery.graphql"
import { AdminMembersReportProductsTable_PaginationFragment$key } from "@/admin/insights/member-reports/tabs/products/__generated__/AdminMembersReportProductsTable_PaginationFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useLabels } from "@/core/context/LabelsContext"
import { useAdminProductLabel } from "@/product/util/hook/useProductLabel"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoTable, DiscoTableSkeletonWithHeader, DiscoText } from "@disco-ui"
import { DiscoTableToolbarStateWithSort } from "@disco-ui/table/header/search/DiscoTableSortDropdown"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { TestIDProps } from "@utils/typeUtils"
import { useState } from "react"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"

export type AdminMembersReportProductsTableToolbarState = DiscoTableToolbarStateWithSort &
  AdminMembersReportProductsTableCurriculumCompletionFilterState

const PRODUCTS_PER_PAGE = 10

type Props = TestIDProps & {
  productType?: "course" | "pathway"
}

function AdminMembersReportProductsTable(props: Props): JSX.Element | null {
  const { productType = "course" } = props
  const testid =
    productType === "pathway"
      ? "AdminMembersReportPathwaysTable"
      : "AdminMembersReportProductsTable"
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const [activePage, setActivePage] = useState(1)
  const [toolbarState, setToolbarState] =
    useState<AdminMembersReportProductsTableToolbarState>({
      curriculumCompletion: null,
      sort: {
        id: "none",
        order: { field: "join_date" },
        title: "Sort",
      },
      direction: "ASC",
    })

  const labels = useLabels()
  const productLabel = useAdminProductLabel(productType)
  const classes = useStyles()
  const activeOrganization = useActiveOrganization()!

  const headers = [
    { value: productLabel.singular },
    { value: "Enrollment Date" },
    ...(productType === "pathway"
      ? [{ value: "Progress" }]
      : [{ value: labels.curriculum.singular }, { value: "Event Attendance" }]),
    { value: "Engagement Score" },
    { value: "Last Active" },
    { value: "Time Spent" },
  ]

  const { node } = useLazyLoadQuery<AdminMembersReportProductsTableQuery>(
    graphql`
      query AdminMembersReportProductsTableQuery(
        $id: ID!
        $first: Int
        $after: String
        $last: Int
        $before: String
        $inProductsViewerIsAdminOf: Boolean
        $productType: String!
        $curriculumCompletion: ProductMembershipsCurriculumCompletionFilter
        $orderBy: ProductMembershipOrder
        $direction: OrderDirection
      ) {
        node(id: $id) {
          ... on OrganizationMembership {
            id
            ...AdminMembersReportProductsTable_PaginationFragment
              @arguments(
                first: $first
                after: $after
                last: $last
                before: $before
                inProductsViewerIsAdminOf: $inProductsViewerIsAdminOf
                productType: $productType
                curriculumCompletion: $curriculumCompletion
                orderBy: $orderBy
                direction: $direction
              )
          }
        }
      }
    `,
    {
      id: queryParams.om!,
      first: PRODUCTS_PER_PAGE,
      curriculumCompletion: toolbarState.curriculumCompletion?.value,
      orderBy: toolbarState.sort.order.field as ProductMembershipOrder,
      direction: toolbarState.direction,
      inProductsViewerIsAdminOf:
        activeOrganization.viewerIsOwnerOrAdmin ||
        activeOrganization.viewerMembership?.hasGroupLeadRole
          ? undefined
          : true,
      productType,
    },
    { fetchPolicy: "network-only" }
  )

  const { data, refetch } = usePaginationFragment<
    AdminMembersReportProductsTablePaginationQuery,
    AdminMembersReportProductsTable_PaginationFragment$key
  >(
    graphql`
      fragment AdminMembersReportProductsTable_PaginationFragment on OrganizationMembership
      @refetchable(queryName: "AdminMembersReportProductsTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        inProductsViewerIsAdminOf: { type: "Boolean" }
        productType: { type: "String!" }
        curriculumCompletion: { type: "ProductMembershipsCurriculumCompletionFilter" }
        orderBy: { type: "ProductMembershipOrder" }
        direction: { type: "OrderDirection" }
      ) {
        id
        productMemberships(
          first: $first
          after: $after
          last: $last
          before: $before
          inProductsViewerIsAdminOf: $inProductsViewerIsAdminOf
          productType: $productType
          curriculumCompletion: $curriculumCompletion
          orderBy: $orderBy
          direction: $direction
        ) @connection(key: "AdminMembersReportProductsTable_productMemberships") {
          totalCount
          edges {
            cursor
            node {
              id
              ...AdminMembersReportProductsTableRowFragment
            }
          }
          pageInfo {
            endCursor
            startCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    node
  )

  if (!data?.productMemberships) return null

  const productMemberships = Relay.connectionToArray(data.productMemberships)
  const cursorsList = data.productMemberships.edges.map((e) => e.cursor)

  return (
    <DiscoTable
      headerButtons={
        <div className={classes.headerButtonsContainer}>
          <DiscoText variant={"heading-xs-600"}>
            {`Registered ${productLabel.plural}`}
          </DiscoText>
          <AdminMembersReportProductsTableToolbar
            testid={testid}
            productType={productType}
            toolbarState={toolbarState}
            setToolbarState={setToolbarState}
          />
        </div>
      }
      headerFilterTags={
        toolbarState.curriculumCompletion && (
          <AdminMembersReportProductsTableFilterTags
            testid={testid}
            toolbarState={toolbarState}
            setToolbarState={
              setToolbarState as React.Dispatch<
                React.SetStateAction<Partial<AdminMembersReportProductsTableToolbarState>>
              >
            }
          />
        )
      }
      filterDeps={[toolbarState]}
      activePage={activePage}
      setActivePage={setActivePage}
      rowsPerPage={PRODUCTS_PER_PAGE}
      scrollable
      testid={testid}
      header={headers}
      rows={productMemberships.map((productMembership, i) => (
        <AdminMembersReportProductsTableRow
          key={productMembership.id}
          testid={`${testid}.row.${i}`}
          productMembershipKey={productMembership}
        />
      ))}
      connection={{
        cursorsList,
        totalCount: data.productMemberships.totalCount,
        pageInfo: {
          endCursor: data.productMemberships.pageInfo.endCursor,
          startCursor: data.productMemberships.pageInfo.startCursor,
        },
      }}
      onPaginate={refetch}
    />
  )
}

const useStyles = makeUseStyles((theme) => ({
  headerButtonsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(1.5),

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      justifyContent: "flex-start",
    },
  },
}))

function AdminMembersReportProductsTableSkeleton({ productType = "course" }: Props) {
  const labels = useLabels()
  const productLabel = useAdminProductLabel(productType)
  const classes = useStyles()

  const headers = [
    { value: productLabel.singular },
    { value: "Enrollment Date" },
    ...(productType === "pathway"
      ? [{ value: "Progress" }]
      : [{ value: labels.curriculum.singular }, { value: "Event Attendance" }]),
    { value: "Engagement Score" },
    { value: "Last Active" },
    { value: "Time Spent" },
  ]
  return (
    <DiscoTableSkeletonWithHeader
      headerButtons={
        <div className={classes.headerButtonsContainer}>
          <DiscoText variant={"heading-xs-600"}>
            {`Registered ${productLabel.plural}`}
          </DiscoText>
          <AdminMembersReportProductsTableToolbarSkeleton />
        </div>
      }
      header={headers}
      rows={productType === "pathway" ? 3 : PRODUCTS_PER_PAGE}
      row={<AdminMembersReportProductsTableRowSkeleton cells={headers.length} />}
    />
  )
}

export default Relay.withSkeleton({
  component: AdminMembersReportProductsTable,
  skeleton: AdminMembersReportProductsTableSkeleton,
})
